import React, { FC } from 'react';
import { Button, Drawer } from 'antd';
import { Notification } from 'common/components';
import { countFormatter } from 'common/helpers';
import { useInventoryContext } from 'common/context';
import { Affiliate, ArrowRightLong, History, User } from 'common/svg';
import { useAppSelector } from 'app/store/store.hooks';
import { EInventoryActionType } from 'entities/Inventory/Inventory.enum';
import { IInventoryAction } from 'entities/InventoryAction/InventoryAction.models';
import {
  getInventoryActionTransferNotificationDescription,
  getInventoryActionWriteOffNotificationDescription,
} from 'entities/Inventory/Inventory.helper';
import { getUserName } from 'entities/User/User.helper';
import { GoodsCardMin } from 'entities/Goods/components';
import { selectInventoryAction, selectIsInventoryActionLoading } from 'entities/InventoryAction/InventoryAction.slice';

interface IComponentProps {
  isIncoming?: boolean;
  onAccept?: (records: IInventoryAction[]) => void;
  onCancel?: (records: IInventoryAction[]) => void;
}

export const InventoryActionCard: FC<IComponentProps> = ({ isIncoming, onAccept, onCancel }) => {
  const inventoryAction = useAppSelector(selectInventoryAction);
  const isInventoryActionLoading = useAppSelector(selectIsInventoryActionLoading);

  const { openInventoryActionCard, setOpenInventoryActionCard } = useInventoryContext();

  const isTransferInventoryAction = inventoryAction?.type === EInventoryActionType.Transfer;
  const isWriteOffInventoryAction = inventoryAction?.type === EInventoryActionType.WriteOff;

  const onClose = () => {
    if (isInventoryActionLoading) {
      return;
    }

    setOpenInventoryActionCard(false);
  };

  if (!inventoryAction) {
    return null;
  }

  return (
    <Drawer
      rootClassName="redesign drawer"
      open={openInventoryActionCard}
      onClose={onClose}
      width={800}
      destroyOnClose
      classNames={{ footer: 'flex align-center justify-between' }}
      footer={
        isIncoming
          ? [
              <Button
                key="cancel"
                className="button-lg secondary"
                onClick={() => onCancel?.([inventoryAction])}
                loading={isInventoryActionLoading}
              >
                Отклонить
              </Button>,
              <Button
                key="accept"
                className="button-lg primary"
                onClick={() => onAccept?.([inventoryAction])}
                loading={isInventoryActionLoading}
              >
                Принять запрос
              </Button>,
            ]
          : [
              <Button
                key="cancel"
                className="button-lg danger"
                onClick={() => onCancel?.([inventoryAction])}
                loading={isInventoryActionLoading}
              >
                Отменить запрос
              </Button>,
            ]
      }
    >
      <div className="drawer__body">
        <div className="ff-regular fs-xxl mb-14">{inventoryAction.good.name}</div>

        <div className="mb-9">
          {inventoryAction.user && (
            <div className="flex align-center mb-4">
              <User className="icon-user-dark-grey mr-3" />

              <span className="ff-medium fs-xs color-dark-grey">
                Ответственный:{' '}
                {getUserName.fullLastNameAndInitials(
                  inventoryAction.user.lastName,
                  inventoryAction.user.firstName,
                  inventoryAction.user.patronymic,
                )}
              </span>
            </div>
          )}

          {inventoryAction.subdivision && (
            <div className="flex align-center mb-4">
              <div className="mr-3">
                <Affiliate className="icon-affiliate-dark-grey" />
              </div>

              <span className="ff-medium fs-xs color-dark-grey">{`Подразделение: ${inventoryAction.subdivision.name}`}</span>
            </div>
          )}

          {isTransferInventoryAction && (
            <div className="flex align-center">
              <div className="mr-3">
                <History className="icon-history-dark-grey" />
              </div>

              <span className="ff-medium fs-xs color-dark-grey">Запрос о передаче {countFormatter(inventoryAction.count)}</span>
            </div>
          )}

          {isWriteOffInventoryAction && (
            <div className="flex align-center">
              <div className="mr-3">
                <ArrowRightLong className="icon-arrow-right-long-dark-grey" />
              </div>

              <span className="ff-medium fs-xs color-dark-grey">Запрос о списании {countFormatter(inventoryAction.count)}</span>
            </div>
          )}
        </div>

        <div className="mb-9">
          <GoodsCardMin good={inventoryAction?.good} canBeOpened={true} />
        </div>

        {isTransferInventoryAction && (
          <div className="mb-9">
            <Notification
              type="success"
              title={`Запрошена передача ${inventoryAction.count} шт`}
              description={getInventoryActionTransferNotificationDescription(inventoryAction)}
            />
          </div>
        )}

        {isWriteOffInventoryAction && (
          <div className="mb-9">
            <Notification
              type="warning"
              title={`Запрошено списание ${inventoryAction.count} шт`}
              description={getInventoryActionWriteOffNotificationDescription(inventoryAction)}
            />
          </div>
        )}
      </div>
    </Drawer>
  );
};
